import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import {
  sendWhatsAppMediaMessage,
  sendWhatsAppMessage,
  sendWhatsAppTemplateMessage,
} from './whatsAppMessageThunks';
import type {
  WhatsAppSendError,
  WhatsAppTemplate,
  WhatsAppWindowConflict,
} from './whatsAppMessageTypes';

/**
 * USE WHATSAPP COMPOSER — logica condivisa fra i due punti da cui si scrive un
 * messaggio WhatsApp: il composer inline dell'accordion (WhatsAppSessionChat) e il
 * modale da scheda contatto (WhatsAppFormModal). Incapsula UN SOLO modo di:
 * - selezionare/allegare un file (stato `file`, input nascosto, apri/pulisci);
 * - inviare, scegliendo fra invio testo e invio media a seconda che ci sia un file,
 *   con toast di successo/errore e stato `submitting`;
 * - reagire alla finestra chiusa di Meta.
 *
 * Sulla finestra chiusa il backend risponde SEMPRE 409, e quello NON è un errore da
 * toast rosso: è la richiesta di confermare l'invio come template. Il 409 porta con
 * sé lo stato della finestra e — se configurato e approvato — il template di
 * ricontatto già pronto, così la UI può mostrare il testo che il contatto riceverà
 * davvero invece di farlo scegliere alla cieca. Il conflitto finisce in
 * `windowConflict` e il chiamante apre il popup.
 *
 * Il server non invia il template da sé: all'inizio lo faceva, ma significava
 * spedire al contatto un testo che l'utente non aveva né scritto né visto.
 *
 * Restano ai due chiamanti solo le differenze reali: il layout, la sorgente del
 * numero (sessione vs combobox) e l'azione post-invio (refresh sessioni vs chiusura
 * modale), gestita guardando il boolean ritornato da {@link send}.
 */
interface WhatsAppSendArgs {
  personId: number;
  phoneNumber: string;
  body: string;
  /** Numero business mittente scelto; assente = numero predefinito della company. */
  phoneNumberId?: string;
}

export function useWhatsAppComposer() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [windowConflict, setWindowConflict] =
    useState<WhatsAppWindowConflict | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Argomenti dell'invio rifiutato, per poterlo rigiocare come template senza
  // richiederli al chiamante.
  const pendingRef = useRef<WhatsAppSendArgs | null>(null);

  // Props da spalmare sull'<input type="file"> nascosto: unico punto in cui il
  // file selezionato entra nello stato.
  const inputProps = {
    ref: inputRef,
    type: 'file' as const,
    hidden: true,
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      setFile(e.target.files?.[0] ?? null),
  };

  const openFilePicker = () => inputRef.current?.click();
  const clearFile = () => setFile(null);
  const dismissWindowConflict = () => setWindowConflict(null);

  /** Testo dell'invio rifiutato: precompila il parametro del template. */
  const pendingBody = () => pendingRef.current?.body ?? '';

  /**
   * Invia il messaggio: media se c'è un file (la caption riusa il testo), altrimenti
   * testo. Ritorna true se l'invio è riuscito, così il chiamante decide la propria
   * azione post-invio.
   *
   * Se il backend rifiuta per finestra chiusa ritorna false SENZA toast d'errore, e
   * valorizza `windowConflict`: il chiamante mostrerà il selettore template.
   */
  async function send({
    personId,
    phoneNumber,
    body,
    phoneNumberId,
  }: WhatsAppSendArgs): Promise<boolean> {
    setSubmitting(true);
    pendingRef.current = { personId, phoneNumber, body, phoneNumberId };
    try {
      try {
        if (file) {
          await dispatch(
            sendWhatsAppMediaMessage({
              personId,
              phoneNumber,
              caption: body.trim() || undefined,
              file,
              phoneNumberId,
            }),
          ).unwrap();
        } else {
          await dispatch(
            sendWhatsAppMessage({ personId, phoneNumber, body, phoneNumberId }),
          ).unwrap();
        }
      } catch (err) {
        const sendError = err as WhatsAppSendError;

        // Finestra chiusa: non è un guasto, è un'altra strada. Sui media però non
        // c'è ripiego (servirebbe un template con header media), quindi resta un
        // errore da mostrare.
        if (sendError?.windowConflict && !file) {
          setWindowConflict(sendError.windowConflict);
          return false;
        }

        toast({
          severity: 'error',
          message: sendError?.message ?? t('whatsapp.error'),
        });
        return false;
      }
      toast({ severity: 'success', message: t('whatsapp.sent') });
      return true;
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Rigioca l'invio rifiutato come template approvato. I parametri sono quelli
   * compilati nel selettore.
   */
  async function sendTemplate(
    template: WhatsAppTemplate,
    parameters: string[],
  ): Promise<boolean> {
    const pending = pendingRef.current;
    if (!pending) return false;

    setSubmitting(true);
    try {
      await dispatch(
        sendWhatsAppTemplateMessage({
          personId: pending.personId,
          phoneNumber: pending.phoneNumber,
          templateName: template.name,
          language: template.language,
          parameters,
          phoneNumberId: pending.phoneNumberId,
        }),
      ).unwrap();
    } catch (err) {
      const sendError = err as WhatsAppSendError;
      toast({
        severity: 'error',
        message: sendError?.message ?? t('whatsapp.error'),
      });
      return false;
    } finally {
      setSubmitting(false);
    }

    setWindowConflict(null);
    toast({ severity: 'success', message: t('whatsapp.template.sent') });
    return true;
  }

  return {
    file,
    inputProps,
    openFilePicker,
    clearFile,
    submitting,
    send,
    windowConflict,
    dismissWindowConflict,
    pendingBody,
    sendTemplate,
  };
}
